import React from 'react';
import styled from 'styled-components';

import ExpandableCopy from './ExpandableCopy';

import additional_shooter_available from '../images/icons/additional_shooter_available.svg';
import brings_pa_equipment from '../images/icons/brings_pa_equipment.svg';
import destination_available from '../images/icons/destination_available.svg';
import digital_editing_included from '../images/icons/digital_editing_included.svg';
import digital_files_included from '../images/icons/digital_files_included.svg';
import discount_days from '../images/icons/discount_days.svg';
import discount_seasons from '../images/icons/discount_seasons.svg';
import drone_available from '../images/icons/drone_available.svg';
import engagement_shoots_available from '../images/icons/engagement_shoots_available.svg';
import is_qualified from '../images/icons/is_qualified.svg';
import is_videographer from '../images/icons/is_videographer.svg';
import photo_album_available from '../images/icons/photo_album_available.svg';
import printing_available from '../images/icons/printing_available.svg';
import service_area from '../images/icons/service_area.svg';
import performs_same_sex from '../images/icons/performs_same_sex.svg';
import performs_religious from '../images/icons/performs_religious.svg';
import performs_non_religious from '../images/icons/performs_non_religious.svg';
import performs_interfaith from '../images/icons/performs_interfaith.svg';
import vowel_assistance from '../images/icons/vowel_assistance.svg';

const icons = {
  additional_shooter_available: {
    icon: additional_shooter_available,
    title: 'Additional Shooter Available',
    description: 'A second photographer can be organised if required.',
  },
  brings_pa_equipment: {
    icon: brings_pa_equipment,
    title: 'Brings PA Equipment',
    description: 'This vendor can bring their own PA system if required.',
  },
  destination_available: {
    icon: destination_available,
    title: 'Available for Destination Weddings',
    description:
      'This vendor is available for destination weddings as well as local weddings.',
  },
  digital_editing_included: {
    icon: digital_editing_included,
    title: 'Digital Editing Included',
    description:
      'Digital editing is included in all packages from this vendor.',
  },
  digital_files_included: {
    icon: digital_files_included,
    title: 'Digital Files Included',
    description:
      'Digital copies are included in all packages from this vendor.',
  },
  discount_days: {
    icon: discount_days,
    title: 'Discount Days',
    description:
      'This vendor offers a discount on days that are less popular like Sundays or Thursdays. ',
  },
  discount_seasons: {
    icon: discount_seasons,
    title: 'Discount Seasons',
    description:
      'This vendor offers a discount on seasons that are less popular like Winter. ',
  },
  drone_available: {
    icon: drone_available,
    title: 'Drone Available',
    description: 'Ask this vendor about their drone options.',
  },
  engagement_shoots_available: {
    icon: engagement_shoots_available,
    title: 'Engagement Shoots Available',
    description: 'Enquire with this vendor about engagement shoots, too.',
  },
  is_qualified: {
    icon: is_qualified,
    title: 'Legally Qualified',
    description:
      'This vendor is legally qualified to perform weddings in their area. ',
  },
  is_videographer: {
    icon: is_videographer,
    title: 'Video Option',
    description:
      'This vendor offers a videography service as well as photography.',
  },
  photo_album_available: {
    icon: photo_album_available,
    title: 'Photo Album Available',
    description:
      'This vendor can offer photo albums at an additional cost unless specified.',
  },
  printing_available: {
    icon: printing_available,
    title: 'Printing Available',
    description:
      'This vendor can offer photo printing at an additional cost unless specified.',
  },
  performs_same_sex: {
    icon: performs_same_sex,
    title: 'Same Sex Ceremonies',
    description: 'This celebrant can perform same sex ceremonies.',
  },
  performs_religious: {
    icon: performs_religious,
    title: 'Religious Ceremonies',
    description:
      'This celebrant can perform religious ceremonies. Enquire for more details on which religions.',
  },
  performs_non_religious: {
    icon: performs_non_religious,
    title: 'Non Religious Ceremonies',
    description: 'This celebrant can perform non religious ceremonies.',
  },
  performs_interfaith: {
    icon: performs_interfaith,
    title: 'Interfaith Ceremonies',
    description:
      'This celebrant can perform interfaith ceremonies. Enquire for more details.',
  },
  vowel_assistance: {
    icon: vowel_assistance,
    title: 'Provides Vow Assistance',
    description: 'This celebrant can perform help write your vows if required.',
  },
  service_area: {
    icon: service_area,
    title: n => `${n}km Service Area`,
    description: n =>
      `This vendor is willing to travel up to ${n}km without changing their price.`,
  },
};

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 2.5em;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Inner = styled.div`
  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: 40px 1fr;
  }
`;

const Title = styled.div`
  text-transform: uppercase;
  margin-bottom: 0.5rem;
  max-width: 220px;

  @media (max-width: 768px) {
    max-width: none;
  }
`;

const Description = styled.p`
  color: #666;
  margin: 1rem 0 0 0;
  max-width: 220px;
  font-size: 0.85rem;
  line-height: 1rem;

  @media (max-width: 768px) {
    max-width: none;
  }
`;

const IconWrapper = styled.div`
  opacity: 0.75;
  @media (max-width: 768px) {
    grid-row: 1 / 3;
  }
`;

const Icon = styled.img`
  height: 45px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1em;
  width: 45px;

  @media (max-width: 768px) {
    height: 26px;
    width: 26px;
  }
`;

export default function VendorDetails({
  details,
  serviceArea = 0,
  hasDiscountDays,
  hasDiscountSeasons,
}) {
  return (
    <Wrapper>
      {details.map((detail, index) => {
        return (
          <Inner key={index}>
            <IconWrapper>
              <Icon src={icons[detail].icon} />
            </IconWrapper>
            <Title>{icons[detail].title}</Title>
            <Description>{icons[detail].description}</Description>
          </Inner>
        );
      })}
      {hasDiscountDays > 0 && (
        <Inner>
          <IconWrapper>
            <Icon src={icons['discount_days'].icon} />
          </IconWrapper>
          <Title>{icons['discount_days'].title}</Title>
          <Description>{icons['discount_days'].description}</Description>
        </Inner>
      )}
      {hasDiscountSeasons > 0 && (
        <Inner>
          <IconWrapper>
            <Icon src={icons['discount_seasons'].icon} />
          </IconWrapper>
          <Title>{icons['discount_seasons'].title}</Title>
          <Description>{icons['discount_seasons'].description}</Description>
        </Inner>
      )}
      {serviceArea > 0 && (
        <Inner>
          <IconWrapper>
            <Icon src={icons['service_area'].icon} />
          </IconWrapper>
          <Title>{icons['service_area'].title(serviceArea)}</Title>
          <Description>
            {icons['service_area'].description(serviceArea)}
          </Description>
        </Inner>
      )}
    </Wrapper>
  );
}
